import type { MissionMode } from "@unfaithful/shared";
import { phoneSound } from "./sound";
import { voiceLine } from "./voiceLine";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  connectGame,
  CueScheduler,
  defaultServerUrl,
  keepClockSynced,
  readJson,
  removeKey,
  request,
  RequestError,
  sendFeedback,
  setReportContext,
  writeJson,
  type GameSocket,
} from "@unfaithful/client";
import type { Cloak, Cue, DiaryUploadRequest, DiaryUploadTicket, GameActionInput, PlayerView, Sigil } from "@unfaithful/shared";

const STORAGE_KEY = "unfaithful.phone.seat";

interface SavedSeat {
  code: string;
  playerId: string;
  rejoinToken: string;
  name: string;
}

/** "stuck": we've been trying for a while; "locked": the sandbox password is missing. */
export type SessionStatus = "connecting" | "online" | "offline" | "stuck" | "locked";

function networkInfo(): string {
  const c = (navigator as unknown as { connection?: { effectiveType?: string; type?: string; downlink?: number } }).connection;
  const parts = [navigator.onLine ? "online" : "offline"];
  if (c?.type) parts.push(c.type);
  if (c?.effectiveType) parts.push(c.effectiveType);
  return parts.join(", ");
}

function buzz(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* not supported (iOS Safari) */
  }
}

/**
 * The phone's connection to its seat. Rejoins automatically after a refresh,
 * a lock screen or a Wi-Fi blip, keeping the same seat (and later, the same secret role).
 */
export function usePlayerSession() {
  const [view, setView] = useState<PlayerView | null>(null);
  const [status, setStatus] = useState<SessionStatus>("connecting");
  const [removedReason, setRemovedReason] = useState<string | null>(null);
  const [lastCue, setLastCue] = useState<Cue | null>(null);
  const [restoring, setRestoring] = useState(() => readJson<SavedSeat>(STORAGE_KEY) !== null);
  const [clockOffset, setClockOffset] = useState(0);
  const socketRef = useRef<GameSocket | null>(null);
  // Settles once we're connected and back in our seat. Coming back from the
  // camera (an iPhone selfie) the connection may have dropped, so anything we
  // send waits for this first.
  const seated = useRef<{ promise: Promise<void>; done: () => void }>(pendingSeat());

  useEffect(() => {
    const socket = connectGame(defaultServerUrl());
    socketRef.current = socket;
    const { clock, stop } = keepClockSynced(socket);
    const scheduler = new CueScheduler(clock, (cue) => {
      // Haptic language (spec): one buzz = look at the TV; two = your turn; long = it's about you.
      if (cue.name === "sync.bell" || cue.name === "phase.enter") buzz(90);
      if (cue.name === "player.prompt" || cue.name === "role.reveal") buzz([70, 60, 70]);
      // Sound too: iPhones can't vibrate from a web page.
      if (cue.name === "sync.bell") phoneSound("bell");
      else if (cue.name === "player.prompt") phoneSound("turn");
      else if (cue.name === "role.reveal") phoneSound("reveal");
      else if (cue.name === "phase.enter") phoneSound("phase");
      else if (cue.name === "mission.result" && cue.payload.potAdded > 0) phoneSound("coin");
      else if (cue.name === "shield.won") {
        phoneSound("right");
        buzz([60, 40, 60, 40, 200]);
      }
      setLastCue(cue);
    });
    const offsetTimer = setInterval(() => setClockOffset(clock.offset), 2000);

    const rejoin = async () => {
      const seat = readJson<SavedSeat>(STORAGE_KEY);
      if (!seat) {
        setRestoring(false);
        return;
      }
      try {
        const res = await request(socket, "player:rejoin", {
          code: seat.code,
          playerId: seat.playerId,
          rejoinToken: seat.rejoinToken,
        });
        setView(res.view);
      } catch (err) {
        if (err instanceof RequestError && err.code !== "timeout") removeKey(STORAGE_KEY);
      } finally {
        setRestoring(false);
        seated.current.done();
      }
    };

    // Dropped connections are reported once we're back, so a test night shows
    // which phones struggled, for how long, and why the browser said it dropped.
    let dropped: { at: number; reason: string; hidden: boolean } | null = null;
    let visibleAt = Date.now();
    let wasHidden = false;
    const trackVisible = () => {
      if (document.visibilityState !== "visible") wasHidden = true;
      else if (wasHidden) {
        wasHidden = false;
        visibleAt = Date.now();
      }
    };
    document.addEventListener("visibilitychange", trackVisible);
    let stuckReported = false;
    socket.on("connect", () => {
      setStatus("online");
      void rejoin().then(() => voiceLine.rejoin(socket));
      if (dropped && readJson<SavedSeat>(STORAGE_KEY)) {
        // Time spent locked or in the camera doesn't count: only the wait the player saw.
        const waited = Math.round((Date.now() - Math.max(dropped.at, visibleAt)) / 1000);
        const total = Math.round((Date.now() - dropped.at) / 1000);
        if (waited >= 3) {
          void sendFeedback({
            kind: "connection",
            app: "phone",
            message: `Lost the castle: ${waited}s to get back in`,
            detail: `reason: ${dropped.reason}; ${dropped.hidden ? `screen was off or in another app (${total}s in all)` : "screen was on"}; ${networkInfo()}`,
          });
        }
      }
      dropped = null;
      stuckReported = false;
    });
    socket.on("disconnect", (reason) => {
      setStatus("offline");
      seated.current = pendingSeat();
      dropped ??= { at: Date.now(), reason, hidden: document.visibilityState !== "visible" };
    });
    socket.on("connect_error", (err) => {
      if (err.message === "sandbox-password") setStatus("locked");
    });

    // Watchdog: a phone back from the camera or the lock screen can sit on a dead
    // connection. If we're not back within a few seconds, start a fresh one;
    // if that doesn't work either, offer a one-tap reload (your seat is saved).
    let downSince: number | null = null;
    const watchdog = setInterval(() => {
      if (socket.connected) {
        downSince = null;
        return;
      }
      if (document.visibilityState !== "visible") return;
      downSince ??= Date.now();
      const down = Date.now() - downSince;
      if (down > 6000 && down < 8500) {
        socket.disconnect();
        socket.connect();
      }
      if (down > 20_000) {
        setStatus((s) => (s === "locked" ? s : "stuck"));
        if (!stuckReported && readJson<SavedSeat>(STORAGE_KEY)) {
          stuckReported = true;
          // keepalive lets this through even if the socket can't connect.
          void sendFeedback({ kind: "connection", app: "phone", message: "Stuck: couldn't get back in for 20s", detail: `reason: ${dropped?.reason ?? "never connected"}; ${networkInfo()}` });
        }
      }
    }, 2000);
    socket.io.on("reconnect_attempt", () => setStatus("connecting"));
    socket.on("view:player", (v) => setView((prev) => (prev && prev.castle.seq > v.castle.seq ? prev : v)));
    socket.on("cue", (cue) => scheduler.schedule(cue));
    socket.on("removed", (reason) => {
      removeKey(STORAGE_KEY);
      setView(null);
      setRemovedReason(reason);
      buzz([80, 60, 80]);
    });

    // Coming back from the lock screen: make sure we're still seated.
    const onVisible = () => {
      if (document.visibilityState === "visible" && !socket.connected) socket.connect();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    window.addEventListener("focus", onVisible);

    return () => {
      clearInterval(offsetTimer);
      clearInterval(watchdog);
      document.removeEventListener("visibilitychange", onVisible);
      document.removeEventListener("visibilitychange", trackVisible);
      window.removeEventListener("pageshow", onVisible);
      window.removeEventListener("focus", onVisible);
      stop();
      scheduler.dispose();
      socket.disconnect();
    };
  }, []);

  // Every report from this phone says which castle, phase and player it came from.
  useEffect(() => {
    setReportContext({ castle: view?.castle.code ?? "", phase: view?.castle.phase ?? "", player: view?.me.name ?? "" });
  }, [view?.castle.code, view?.castle.phase, view?.me.name]);

  const join = useCallback(async (code: string, name: string): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Still connecting. Try again in a moment.";
    try {
      const res = await request(socket, "player:join", { code, name });
      writeJson(STORAGE_KEY, { code: res.view.castle.code, playerId: res.playerId, rejoinToken: res.rejoinToken, name } satisfies SavedSeat);
      setRemovedReason(null);
      setView(res.view);
      buzz(60);
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't reach the castle.";
    }
  }, []);

  const restyle = useCallback(async (sigil: Sigil, cloak: Cloak): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      const res = await request(socket, "player:style", { sigil, cloak });
      setView(res.view);
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't change your look.";
    }
  }, []);

  const leave = useCallback(async () => {
    const socket = socketRef.current;
    removeKey(STORAGE_KEY);
    setView(null);
    if (socket) await request(socket, "player:leave").catch(() => undefined);
  }, []);

  /** The Host ends the castle for everyone. */
  const end = useCallback(async () => {
    const socket = socketRef.current;
    if (socket) await request(socket, "host:end").catch(() => undefined);
    removeKey(STORAGE_KEY);
    setView(null);
  }, []);

  const ringBell = useCallback(async (): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "host:sync-bell");
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "The bell didn't ring.";
    }
  }, []);

  const setLobby = useCallback(async (req: { missionMode?: MissionMode; familyMode?: boolean }): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "host:lobby", req);
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't change that.";
    }
  }, []);

  const transferHost = useCallback(async (playerId: string): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "host:transfer", { playerId });
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't hand over.";
    }
  }, []);

  const kick = useCallback(async (playerId: string): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "host:kick", { playerId });
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't remove that player.";
    }
  }, []);

  /** Your optional selfie (a shrunk JPEG data URL), or null to remove it. */
  const setPhoto = useCallback(async (image: string | null): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      if (!socket.connected) socket.connect();
      await Promise.race([seated.current.promise, new Promise((r) => setTimeout(r, 12_000))]);
      await request(socket, "player:photo", { image });
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Your photo didn't send.";
    }
  }, []);

  /** Your optional three facts. Returns an error message or null. */
  const setFacts = useCallback(async (facts: string[]): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "player:facts", { facts });
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Those didn't save.";
    }
  }, []);

  /** Host: photos on or off for this castle. */
  const setPhotosEnabled = useCallback(async (enabled: boolean): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "host:photos", { enabled });
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't change that.";
    }
  }, []);

  /** Host, sandbox only: fill seats with test guests. */
  const addTestGuests = useCallback(async (count: number): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "host:test-guests", { count });
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't add test guests.";
    }
  }, []);

  const act = useCallback(async (action: GameActionInput): Promise<string | null> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      await request(socket, "game:act", action);
      return null;
    } catch (err) {
      return err instanceof RequestError ? err.message : "That didn't work.";
    }
  }, []);

  /** Ask the castle where to upload today's diary. */
  const requestUpload = useCallback(async (req: DiaryUploadRequest): Promise<DiaryUploadTicket | string> => {
    const socket = socketRef.current;
    if (!socket) return "Not connected.";
    try {
      return await request(socket, "diary:upload", req);
    } catch (err) {
      return err instanceof RequestError ? err.message : "Couldn't start the upload.";
    }
  }, []);

  const joinVoice = useCallback(() => {
    if (socketRef.current) void voiceLine.join(socketRef.current);
  }, []);
  const leaveVoice = useCallback(() => voiceLine.leave(), []);

  return { view, status, restoring, removedReason, lastCue, clockOffset, joinVoice, leaveVoice, join, restyle, leave, end, ringBell, kick, setLobby, transferHost, addTestGuests, setPhoto, setPhotosEnabled, setFacts, act, requestUpload };
}

function pendingSeat(): { promise: Promise<void>; done: () => void } {
  let done = () => {};
  const promise = new Promise<void>((resolve) => (done = resolve));
  return { promise, done };
}
